import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset
} from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Create/update user profile in Firestore
    await syncUserProfile(user);
    
    return user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export const syncUserProfile = async (user: User) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      let role = 'user';
      if (user.email === 'sahin.siddique@gmail.com') {
        role = 'super_admin';
      } else {
        // Check if email is in admin_users collection
        const adminRef = doc(db, 'admin_users', user.email!);
        const adminSnap = await getDoc(adminRef);
        if (adminSnap.exists() && adminSnap.data().status === 'active') {
          role = 'admin';
        }
      }

      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || (user.email ? user.email.split('@')[0] : 'User'),
        photoURL: user.photoURL || null,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        role: role,
        status: 'active',
        plan: 'free',
        credits: 0,
        reports_used: 0,
        paymentStatus: 'none',
        stripeCustomerId: null,
        lastStripeSessionId: null,
        purchasedAt: null,
        updatedAt: serverTimestamp(),
        proStatus: 'inactive',
        proGrantedBy: null,
        proGrantedAt: null,
        proExpiresAt: null,
        // Finance schema placeholders
        payoutMethod: null,
        bankDetails: null,
        refundStatus: null,
        paymentHistory: []
      });
    } else {
      const updateData: any = {
        lastLogin: serverTimestamp()
      };
      if (user.displayName) {
        updateData.displayName = user.displayName;
      }
      if (user.photoURL) {
        updateData.photoURL = user.photoURL;
      }
      await setDoc(userRef, updateData, { merge: true });
    }
  } catch (error) {
    console.error('Error syncing user profile to Firestore:', error);
    // We don't throw here to allow the user to still be logged in
  }
};

export const logOut = () => signOut(auth);

export const signUpWithEmail = async (email: string, pass: string) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, pass);
    const user = result.user;
    
    await syncUserProfile(user);
    await sendEmailVerification(user);
    
    return user;
  } catch (error) {
    console.error('Error signing up with email:', error);
    throw error;
  }
};

export const signInWithEmail = async (email: string, pass: string) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, pass);
    const user = result.user;
    
    await syncUserProfile(user);
    
    return user;
  } catch (error) {
    console.error('Error signing in with email:', error);
    throw error;
  }
};

export const resendVerificationEmail = async (user: User) => {
  try {
    await sendEmailVerification(user);
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

export const completePasswordReset = async (oobCode: string, newPass: string) => {
  try {
    await confirmPasswordReset(auth, oobCode, newPass);
  } catch (error) {
    console.error('Error completing password reset:', error);
    throw error;
  }
};

export { onAuthStateChanged };
export type { User };
